import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Zap, TrendingUp, TrendingDown, AlertTriangle,
  Trash2, Loader2, ArrowUpRight, ArrowDownRight, Minus,
  Pencil, X, Save, Plus,
} from 'lucide-react';
import { toast } from 'sonner';

const TEST_LABELS = {
  SJ: 'Squat Jump', CMJ: 'Counter Movement Jump', CMJ_RIGHT_LEFT: 'CMJ Right/Left',
  LOADED_CMJ: 'Loaded CMJ', DROP_JUMP: 'Drop Jump',
  CMJ_FREE_ARMS: 'CMJ Free Arms', DRIFT_2D: 'Drift 2D', STIFFNESS: 'Stiffness',
};

const WRITE_ROLES = ['ADMIN', 'SNC', 'PHYSIO', 'COACH'];

export default function JumpSessionDetailPage() {
  const { sessionId } = useParams();
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (en, it) => language === 'IT' ? it : en;
  const canEdit = WRITE_ROLES.includes(user?.role);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparisons, setComparisons] = useState({});

  // Edit-mode state
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [metaDraft, setMetaDraft] = useState({ date: '', notes: '' });
  const [resultDrafts, setResultDrafts] = useState({}); // { result_id: {jump_height_cm, rsi, side, load_kg, trial_number, notes} }
  const [deletedIds, setDeletedIds] = useState(new Set());
  const [newRows, setNewRows] = useState([]); // [{tempId, athlete_id, jump_height_cm, rsi, side, load_kg, trial_number, notes}]
  const [teamAthletes, setTeamAthletes] = useState([]);

  const fetchSession = useCallback(async () => {
    try {
      const r = await api.get(`/jump/sessions/${sessionId}`);
      setSession(r.data);
      setMetaDraft({ date: r.data.date || '', notes: r.data.notes || '' });
      setResultDrafts({});
      setDeletedIds(new Set());
      setNewRows([]);
    } catch { navigate('/staff/jump'); }
    setLoading(false);
  }, [api, sessionId, navigate]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  // Fetch athletes for "Add athlete" when we enter edit mode
  useEffect(() => {
    if (!editMode || !session?.team_id) return;
    api.get(`/athletes?team_id=${session.team_id}`).then(r => setTeamAthletes(r.data || []));
  }, [editMode, api, session?.team_id]);

  // Comparisons for best trials (view mode)
  useEffect(() => {
    if (editMode || !session?.results?.length) return;
    const bestResults = session.results.filter(r => r.best_trial);
    if (!bestResults.length) return;
    const fetchComps = async () => {
      const comps = {};
      for (const r of bestResults) {
        try {
          const res = await api.get(`/jump/results/${r.id}/comparison`);
          comps[r.id] = res.data;
        } catch { comps[r.id] = { has_previous: false }; }
      }
      setComparisons(comps);
    };
    fetchComps();
  }, [api, session, editMode]);

  const enterEdit = () => {
    setEditMode(true);
    setMetaDraft({ date: session.date || '', notes: session.notes || '' });
    setResultDrafts({});
    setDeletedIds(new Set());
    setNewRows([]);
  };
  const cancelEdit = () => {
    setEditMode(false);
    fetchSession();
  };

  const updateResultDraft = (rid, field, value) => {
    setResultDrafts(prev => ({ ...prev, [rid]: { ...(prev[rid] || {}), [field]: value } }));
  };
  const markResultDeleted = (rid) => setDeletedIds(prev => new Set(prev).add(rid));
  const undoResultDeleted = (rid) => setDeletedIds(prev => { const n = new Set(prev); n.delete(rid); return n; });

  const addNewRow = () => {
    setNewRows(prev => [...prev, {
      tempId: `new-${Date.now()}-${Math.random()}`,
      athlete_id: '', trial_number: 1, jump_height_cm: '', rsi: '',
      side: session.test_type === 'CMJ_RIGHT_LEFT' ? 'RIGHT' : '',
      load_kg: '', notes: '',
    }]);
  };
  const updateNewRow = (tempId, field, value) => {
    setNewRows(prev => prev.map(r => r.tempId === tempId ? { ...r, [field]: value } : r));
  };
  const removeNewRow = (tempId) => setNewRows(prev => prev.filter(r => r.tempId !== tempId));

  const toNum = (v, fn = parseFloat) => (v !== '' && v != null) ? (isNaN(fn(v)) ? null : fn(v)) : null;

  const handleSave = async () => {
    setSaving(true);
    const errors = [];

    // 1. Session metadata (only if changed)
    const metaChanged = metaDraft.date !== session.date || metaDraft.notes !== (session.notes || '');
    if (metaChanged) {
      try {
        await api.put(`/jump/sessions/${sessionId}`, {
          date: metaDraft.date,
          notes: metaDraft.notes,
        });
      } catch (e) {
        errors.push(t('Failed to update session metadata', 'Aggiornamento sessione fallito') + `: ${e.response?.data?.detail || e.message}`);
      }
    }

    // 2. Delete removed results
    for (const rid of deletedIds) {
      try { await api.delete(`/jump/results/${rid}`); }
      catch (e) { errors.push(`Delete ${rid.slice(0, 8)}: ${e.response?.data?.detail || e.message}`); }
    }

    // 3. Update edited results
    for (const [rid, draft] of Object.entries(resultDrafts)) {
      if (deletedIds.has(rid)) continue;
      const payload = {};
      const editable = ['trial_number', 'side', 'load_kg', 'jump_height_cm', 'flight_time_ms',
        'contact_time_ms', 'rsi', 'notes'];
      for (const k of editable) {
        if (k in draft) {
          if (['side', 'notes'].includes(k)) payload[k] = draft[k] || null;
          else payload[k] = toNum(draft[k], k === 'trial_number' ? parseInt : parseFloat);
        }
      }
      if (Object.keys(payload).length) {
        try { await api.put(`/jump/results/${rid}`, payload); }
        catch (e) { errors.push(`Update ${rid.slice(0, 8)}: ${e.response?.data?.detail || e.message}`); }
      }
    }

    // 4. Add new athlete rows
    for (const row of newRows) {
      if (!row.athlete_id) {
        errors.push(t('New row missing athlete', 'Riga senza atleta'));
        continue;
      }
      const payload = {
        athlete_id: row.athlete_id,
        trial_number: toNum(row.trial_number, parseInt) || 1,
        jump_height_cm: toNum(row.jump_height_cm),
        rsi: toNum(row.rsi),
        side: row.side || null,
        load_kg: toNum(row.load_kg),
        notes: row.notes || null,
      };
      try { await api.post(`/jump/sessions/${sessionId}/results`, payload); }
      catch (e) { errors.push(`Add athlete ${row.athlete_id.slice(0, 8)}: ${e.response?.data?.detail || e.message}`); }
    }

    // 5. If session was COMPLETED, force a re-finalize so summary+alerts refresh
    //    (also handled by backend PUT/DELETE recompute helper, but re-finalizing
    //    a session that already had status=COMPLETED is a no-op safe fallback)
    // Backend PUT/DELETE already triggers _recompute_session_derived → skip.

    // Refresh canonical data from server regardless of errors so UI reflects true state.
    await fetchSession();

    setSaving(false);
    if (errors.length) {
      toast.error(t('Some changes failed', 'Alcune modifiche non salvate') + `: ${errors.slice(0, 3).join(' | ')}`);
    } else {
      toast.success(t('Session updated', 'Sessione aggiornata'));
      setEditMode(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('Delete this session?', 'Eliminare questa sessione?'))) return;
    try {
      await api.delete(`/jump/sessions/${sessionId}`);
      toast.success(t('Session deleted', 'Sessione eliminata'));
      navigate('/staff/jump');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (!session) return null;

  const s = session.summary;
  const results = session.results || [];
  const isSided = session.test_type === 'CMJ_RIGHT_LEFT' || session.test_type === 'DRIFT_2D';
  const isDropJump = session.test_type === 'DROP_JUMP';
  const isDrift = session.test_type === 'DRIFT_2D';
  const isStiff = session.test_type === 'STIFFNESS';

  // Group results by athlete
  const byAthlete = {};
  results.forEach(r => {
    if (!byAthlete[r.athlete_id]) byAthlete[r.athlete_id] = { name: r.athlete_name, results: [] };
    byAthlete[r.athlete_id].results.push(r);
  });

  const existingAthleteIds = new Set(Object.keys(byAthlete));
  const availableToAdd = teamAthletes.filter(a => !existingAthleteIds.has(a.id));

  return (
    <div className="space-y-6 fade-in min-w-0 overflow-x-hidden" data-testid="jump-session-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate('/staff/jump')} className="text-zinc-400 hover:text-zinc-100">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-zinc-100 flex items-center gap-2">
              <Zap className="w-5 h-5 text-cyan-400" />
              {TEST_LABELS[session.test_type] || session.test_type}
            </h1>
            <p className="text-sm text-zinc-500">{session.date} • {session.team_code || ''} • {Object.keys(byAthlete).length} {t('athletes', 'atleti')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!editMode && canEdit && (
            <Button variant="outline" size="sm" onClick={enterEdit}
              className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10" data-testid="edit-session-btn">
              <Pencil className="w-4 h-4 mr-1" /> {t('Edit', 'Modifica')}
            </Button>
          )}
          {editMode && (
            <>
              <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saving}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="cancel-edit-btn">
                <X className="w-4 h-4 mr-1" /> {t('Cancel', 'Annulla')}
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-edit-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                {t('Save', 'Salva')}
              </Button>
            </>
          )}
          {!editMode && canEdit && (
            <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-400 hover:text-red-300" data-testid="delete-session-btn">
              <Trash2 className="w-4 h-4 mr-1" /> {t('Delete', 'Elimina')}
            </Button>
          )}
        </div>
      </div>

      {/* Session metadata: view or edit */}
      {editMode ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base">{t('Session', 'Sessione')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-zinc-500 uppercase">{t('Date', 'Data')}</label>
              <Input type="date" value={metaDraft.date} onChange={e => setMetaDraft({ ...metaDraft, date: e.target.value })}
                className="bg-zinc-950 border-zinc-700 text-zinc-100 min-h-[44px]" data-testid="edit-session-date" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-zinc-500 uppercase">{t('Notes', 'Note')}</label>
              <Textarea value={metaDraft.notes} onChange={e => setMetaDraft({ ...metaDraft, notes: e.target.value })}
                rows={2} className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="edit-session-notes" />
            </div>
          </CardContent>
        </Card>
      ) : (
        session.notes && <p className="text-sm text-zinc-400 italic">{session.notes}</p>
      )}

      {/* Summary (hidden in edit mode to reduce noise) */}
      {!editMode && s && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {s.team_avg_height != null && (
            <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
              <div className="text-xl font-bold text-zinc-100">{s.team_avg_height} cm</div>
              <div className="text-xs text-zinc-500">{t('Team Avg', 'Media')}</div>
            </CardContent></Card>
          )}
          {s.best_height != null && (
            <Card className="bg-emerald-500/10 border-emerald-500/30"><CardContent className="p-4 text-center">
              <TrendingUp className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
              <div className="text-xl font-bold text-emerald-400" data-testid="summary-best-height">{s.best_height} cm</div>
              <div className="text-xs text-zinc-500">{t('Best', 'Migliore')}</div>
            </CardContent></Card>
          )}
          {s.lowest_height != null && (
            <Card className="bg-red-500/10 border-red-500/30"><CardContent className="p-4 text-center">
              <TrendingDown className="w-4 h-4 text-red-400 mx-auto mb-1" />
              <div className="text-xl font-bold text-red-400">{s.lowest_height} cm</div>
              <div className="text-xs text-zinc-500">{t('Lowest', 'Minore')}</div>
            </CardContent></Card>
          )}
          {s.team_avg_rsi != null && (
            <Card className="bg-cyan-500/10 border-cyan-500/30"><CardContent className="p-4 text-center">
              <div className="text-xl font-bold text-cyan-400">{s.team_avg_rsi}</div>
              <div className="text-xs text-zinc-500">Avg RSI</div>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* iter_119 — Per-athlete DRIFT_2D LEFT/RIGHT cards */}
      {!editMode && isDrift && s?.athlete_summaries && (
        <div className="space-y-4" data-testid="drift-summary">
          {Object.entries(s.athlete_summaries).map(([aid, a]) => (
            <Card key={aid} className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2"><CardTitle className="text-zinc-200 text-base">{a.athlete_name || byAthlete[aid]?.name || aid}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {['left','right'].map((side) => (
                    <div key={side} className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3" data-testid={`drift-${side}-${aid}`}>
                      <div className="text-xs font-semibold text-cyan-400 uppercase mb-2">{side === 'left' ? t('Left', 'Sinistra') : t('Right', 'Destra')}</div>
                      {a[side] ? (
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-zinc-500 text-xs">{t('Mean H', 'H media')}</span><div className="text-zinc-100 font-mono">{a[side].mean_height_cm != null ? `${a[side].mean_height_cm} cm` : '—'}</div></div>
                          <div><span className="text-zinc-500 text-xs">{t('Mean CT', 'CT media')}</span><div className="text-zinc-100 font-mono">{a[side].mean_contact_time_s != null ? `${a[side].mean_contact_time_s} s` : '—'}</div></div>
                          <div><span className="text-zinc-500 text-xs">Area</span><div className="text-zinc-100 font-mono">{a[side].area_cm2 != null ? `${a[side].area_cm2} cm²` : '—'}</div></div>
                          <div><span className="text-zinc-500 text-xs">Power Density</span><div className="text-zinc-100 font-mono">{a[side].power_density_w_kg_dm2 != null ? `${a[side].power_density_w_kg_dm2} W/kg/dm²` : '—'}</div></div>
                        </div>
                      ) : (<div className="text-xs text-zinc-500 italic">{t('Not recorded', 'Non registrato')}</div>)}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* iter_119 — Per-athlete STIFFNESS summary */}
      {!editMode && isStiff && s?.athlete_summaries && (
        <div className="space-y-4" data-testid="stiffness-summary">
          {Object.entries(s.athlete_summaries).map(([aid, a]) => (
            <Card key={aid} className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2"><CardTitle className="text-zinc-200 text-base">{a.athlete_name || byAthlete[aid]?.name || aid}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-zinc-500 text-xs">{t('Mean CT', 'CT media')} *</span><div className="text-zinc-100 font-mono">{a.mean_contact_time_s != null ? `${a.mean_contact_time_s} s` : '—'}</div></div>
                  <div><span className="text-zinc-500 text-xs">{t('Mean FT', 'FT media')} *</span><div className="text-zinc-100 font-mono">{a.mean_flight_time_s != null ? `${a.mean_flight_time_s} s` : '—'}</div></div>
                  <div><span className="text-zinc-500 text-xs">{t('Min CT', 'CT min')}</span><div className="text-zinc-100 font-mono">{a.min_contact_time_s != null ? `${a.min_contact_time_s} s` : '—'}</div></div>
                  <div><span className="text-zinc-500 text-xs">{t('Max FT', 'FT max')}</span><div className="text-zinc-100 font-mono">{a.max_flight_time_s != null ? `${a.max_flight_time_s} s` : '—'}</div></div>
                </div>
                <div className="mt-3 pt-3 border-t border-zinc-800/50 flex items-center justify-between"
                     title={t('Mean Flight ÷ Mean Contact', 'Tempo di volo medio ÷ Tempo di contatto medio')}>
                  <span className="text-xs text-cyan-400 font-semibold">{t('Flight/Contact Ratio', 'Rapporto volo/contatto')}</span>
                  <span className="text-sm text-cyan-300 font-mono" data-testid={`stiff-ratio-${aid}`}>
                    {a.flight_contact_ratio != null ? a.flight_contact_ratio : '—'}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Alerts (hidden in edit mode) */}
      {!editMode && session.alerts?.length > 0 && (
        <Card className="bg-red-500/5 border-red-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-400 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {t('Alerts', 'Allerte')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {session.alerts.map((a, i) => (
              <div key={i} className="text-sm flex items-center justify-between p-2 bg-red-500/5 rounded-sm">
                <span className="text-zinc-300">{a.athlete_name}</span>
                <span className="text-red-400 text-xs">
                  {a.type === 'PERFORMANCE_DROP' && `Drop ${a.drop_pct}%`}
                  {a.type === 'ASYMMETRY' && `Asymmetry ${a.asymmetry_pct}%`}
                  {a.type === 'RSI_LOW' && `RSI ${a.rsi}`}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Athlete Results Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-zinc-100 text-base">{t('Results by Athlete', 'Risultati per Atleta')}</CardTitle>
          {editMode && (
            <Button size="sm" variant="outline" onClick={addNewRow} disabled={availableToAdd.length === 0}
              className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
              data-testid="add-athlete-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> {t('Add Athlete', 'Aggiungi atleta')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(byAthlete).map(([aid, { name, results: ars }]) => {
            const best = ars.find(r => r.best_trial);
            return (
              <div key={aid} className="p-3 bg-zinc-950/50 rounded-sm border border-zinc-800" data-testid={`athlete-block-${aid}`}>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <Link to={`/staff/jump/athlete/${aid}`} className="text-sm font-medium text-zinc-200 hover:text-cyan-400 transition-colors"
                    data-testid={`athlete-link-${aid}`}>{name}</Link>
                  {!editMode && best && (
                    <span className="text-xs text-cyan-400 font-semibold">
                      {t('Best', 'Best')}: {best.jump_height_cm}cm
                      {best.rsi != null && ` | RSI ${best.rsi}`}
                    </span>
                  )}
                </div>
                {!editMode && best && comparisons[best.id]?.has_previous && (() => {
                  const c = comparisons[best.id];
                  const hd = c.deltas?.height;
                  if (!hd) return null;
                  const color = hd.direction === 'improved' ? 'text-emerald-400' : hd.direction === 'declined' ? 'text-rose-400' : 'text-zinc-400';
                  const Icon = hd.direction === 'improved' ? ArrowUpRight : hd.direction === 'declined' ? ArrowDownRight : Minus;
                  return (
                    <div className={`text-[11px] mb-2 flex items-center gap-1 ${color}`} data-testid={`comparison-line-${aid}`}>
                      <Icon className="w-3 h-3" />
                      <span>vs prev: {hd.abs > 0 ? '+' : ''}{hd.abs.toFixed(1)}cm ({hd.pct > 0 ? '+' : ''}{hd.pct}%)</span>
                      {c.deltas?.rsi && <span className="text-zinc-500 ml-1">| RSI {c.deltas.rsi.pct > 0 ? '+' : ''}{c.deltas.rsi.pct}%</span>}
                    </div>
                  );
                })()}

                {/* Results — view or edit */}
                {editMode && isDrift ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`edit-drift-${aid}`}>
                    {['LEFT','RIGHT'].map((side) => {
                      const row = ars.find((r) => r.side === side);
                      if (!row) return (
                        <div key={side} className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3 text-xs text-zinc-500 italic">
                          {side} — {t('no row (create via Group Test)', 'nessuna riga (crea da Group Test)')}
                        </div>
                      );
                      const isDeleted = deletedIds.has(row.id);
                      const draft = resultDrafts[row.id] || {};
                      const val = (k) => (k in draft ? draft[k] : (row[k] ?? ''));
                      return (
                        <div key={side} className={`rounded-md border border-zinc-800 bg-zinc-950/40 p-3 space-y-2 ${isDeleted ? 'opacity-30 line-through' : ''}`} data-testid={`edit-drift-${side.toLowerCase()}-${aid}`}>
                          <div className="text-xs font-semibold text-cyan-400 uppercase">{side === 'LEFT' ? t('Left', 'Sinistra') : t('Right', 'Destra')}</div>
                          {[
                            ['mean_height_cm', t('Mean Height (cm)', 'H media (cm)'), 0.1],
                            ['mean_contact_time_s', t('Mean Contact (s)', 'CT media (s)'), 0.01],
                            ['area_cm2', 'Area (cm²)', 0.1],
                            ['power_density_w_kg_dm2', 'Power Density (W/kg/dm²)', 0.1],
                          ].map(([f, label, step]) => (
                            <div key={f} className="flex items-center justify-between gap-2">
                              <label className="text-xs text-zinc-400 flex-1">{label}</label>
                              <Input type="number" step={step} value={val(f) ?? ''}
                                disabled={isDeleted}
                                onChange={(e) => updateResultDraft(row.id, f, e.target.value)}
                                className="h-8 w-24 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs text-right"
                                data-testid={`edit-drift-${f}-${row.id}`} />
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ) : editMode && isStiff ? (
                  (() => {
                    const row = ars[0];
                    if (!row) return null;
                    const isDeleted = deletedIds.has(row.id);
                    const draft = resultDrafts[row.id] || {};
                    const val = (k) => (k in draft ? draft[k] : (row[k] ?? ''));
                    const mc = parseFloat(val('mean_contact_time_s'));
                    const mf = parseFloat(val('mean_flight_time_s'));
                    const ratio = (!isNaN(mc) && !isNaN(mf) && mc > 0) ? (mf / mc).toFixed(3) : '—';
                    return (
                      <div className={`rounded-md border border-zinc-800 bg-zinc-950/40 p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 ${isDeleted ? 'opacity-30 line-through' : ''}`} data-testid={`edit-stiff-${aid}`}>
                        {[
                          ['mean_contact_time_s', t('Mean Contact (s) *', 'CT media (s) *'), 0.01, true],
                          ['mean_flight_time_s', t('Mean Flight (s) *', 'FT media (s) *'), 0.01, true],
                          ['min_contact_time_s', t('Min Contact (s)', 'CT min (s)'), 0.01, false],
                          ['max_flight_time_s', t('Max Flight (s)', 'FT max (s)'), 0.01, false],
                        ].map(([f, label, step, req]) => (
                          <div key={f} className="flex items-center justify-between gap-2">
                            <label className="text-xs text-zinc-400 flex-1">{label}</label>
                            <Input type="number" step={step} value={val(f) ?? ''}
                              disabled={isDeleted}
                              onChange={(e) => updateResultDraft(row.id, f, e.target.value === '' ? null : e.target.value)}
                              className="h-8 w-24 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs text-right"
                              data-testid={`edit-stiff-${f}-${row.id}`} />
                          </div>
                        ))}
                        <div className="col-span-full flex items-center justify-between gap-2 pt-2 border-t border-zinc-800/50" title={t('Mean Flight ÷ Mean Contact', 'Tempo di volo medio ÷ Tempo di contatto medio')}>
                          <span className="text-xs text-cyan-400 font-semibold">{t('Flight/Contact Ratio', 'Rapporto volo/contatto')}</span>
                          <span className="text-sm text-cyan-300 font-mono" data-testid={`edit-stiff-ratio-${row.id}`}>{ratio}</span>
                        </div>
                      </div>
                    );
                  })()
                ) : editMode ? (
                  <div className="overflow-x-auto -mx-2 px-2">
                    <table className="w-full text-xs min-w-[520px]" data-testid={`edit-table-${aid}`}>
                      <thead>
                        <tr className="text-zinc-500 uppercase">
                          <th className="text-left py-1 pr-2">T#</th>
                          {isSided && <th className="text-left py-1 pr-2">Side</th>}
                          <th className="text-left py-1 pr-2">Load</th>
                          <th className="text-left py-1 pr-2">Height (cm)</th>
                          {isDropJump && <th className="text-left py-1 pr-2">Contact (ms)</th>}
                          <th className="text-left py-1 pr-2">RSI</th>
                          <th className="text-right py-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {ars.sort((a, b) => a.trial_number - b.trial_number).map(r => {
                          const isDeleted = deletedIds.has(r.id);
                          const draft = resultDrafts[r.id] || {};
                          const val = (k, fallback) => (k in draft ? draft[k] : (r[k] ?? fallback));
                          return (
                            <tr key={r.id} className={isDeleted ? 'opacity-30 line-through' : ''} data-testid={`edit-row-${r.id}`}>
                              <td className="py-1 pr-2">
                                <Input type="number" min="1" value={val('trial_number', 1) || ''}
                                  disabled={isDeleted}
                                  onChange={e => updateResultDraft(r.id, 'trial_number', e.target.value)}
                                  className="h-9 w-14 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs"
                                  data-testid={`edit-trial-${r.id}`} />
                              </td>
                              {isSided && (
                                <td className="py-1 pr-2">
                                  <Select value={val('side', '') || ''}
                                    disabled={isDeleted}
                                    onValueChange={v => updateResultDraft(r.id, 'side', v)}>
                                    <SelectTrigger className="h-9 w-20 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs" data-testid={`edit-side-${r.id}`}>
                                      <SelectValue placeholder="—" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-700">
                                      <SelectItem value="RIGHT">R</SelectItem>
                                      <SelectItem value="LEFT">L</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                              )}
                              <td className="py-1 pr-2">
                                <Input type="number" step="0.5" value={val('load_kg', '') ?? ''}
                                  disabled={isDeleted}
                                  onChange={e => updateResultDraft(r.id, 'load_kg', e.target.value)}
                                  className="h-9 w-16 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs" />
                              </td>
                              <td className="py-1 pr-2">
                                <Input type="number" step="0.1" value={val('jump_height_cm', '') ?? ''}
                                  disabled={isDeleted}
                                  onChange={e => updateResultDraft(r.id, 'jump_height_cm', e.target.value)}
                                  className="h-9 w-20 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs"
                                  data-testid={`edit-height-${r.id}`} />
                              </td>
                              {isDropJump && (
                                <td className="py-1 pr-2">
                                  <Input type="number" step="1" value={val('contact_time_ms', '') ?? ''}
                                    disabled={isDeleted}
                                    onChange={e => updateResultDraft(r.id, 'contact_time_ms', e.target.value)}
                                    className="h-9 w-20 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs" />
                                </td>
                              )}
                              <td className="py-1 pr-2">
                                <Input type="number" step="0.01" value={val('rsi', '') ?? ''}
                                  disabled={isDeleted}
                                  onChange={e => updateResultDraft(r.id, 'rsi', e.target.value)}
                                  className="h-9 w-16 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs" />
                              </td>
                              <td className="py-1 text-right">
                                {isDeleted ? (
                                  <Button size="sm" variant="ghost" onClick={() => undoResultDeleted(r.id)}
                                    className="text-emerald-400 hover:text-emerald-300 h-8 px-2">
                                    {t('Undo', 'Annulla')}
                                  </Button>
                                ) : (
                                  <Button size="sm" variant="ghost" onClick={() => markResultDeleted(r.id)}
                                    className="text-red-400 hover:text-red-300 h-8 px-2"
                                    data-testid={`delete-result-${r.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {ars.sort((a, b) => a.trial_number - b.trial_number).map(r => (
                      <div key={r.id} className={`px-2 py-1 rounded text-xs border ${
                        r.best_trial ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-300' : 'border-zinc-800 bg-zinc-900 text-zinc-400'
                      }`}>
                        T{r.trial_number}{r.side ? ` ${r.side[0]}` : ''}{r.load_kg != null ? ` ${r.load_kg}kg` : ''}: {r.jump_height_cm}cm
                        {r.rsi != null && ` RSI:${r.rsi}`}
                        {r.best_trial && ' ★'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* New athlete rows */}
          {editMode && newRows.length > 0 && (
            <div className="p-3 bg-emerald-500/5 rounded-sm border border-emerald-500/30" data-testid="new-rows">
              <div className="text-xs font-semibold text-emerald-400 uppercase mb-2">{t('New Results', 'Nuovi risultati')}</div>
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full text-xs min-w-[600px]">
                  <thead>
                    <tr className="text-zinc-500 uppercase">
                      <th className="text-left py-1 pr-2">Athlete</th>
                      <th className="text-left py-1 pr-2">T#</th>
                      {isSided && <th className="text-left py-1 pr-2">Side</th>}
                      <th className="text-left py-1 pr-2">Load</th>
                      <th className="text-left py-1 pr-2">Height (cm)</th>
                      {isDropJump && <th className="text-left py-1 pr-2">Contact (ms)</th>}
                      <th className="text-left py-1 pr-2">RSI</th>
                      <th className="text-right py-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newRows.map((row) => (
                      <tr key={row.tempId} data-testid={`new-row-${row.tempId}`}>
                        <td className="py-1 pr-2">
                          <Select value={row.athlete_id || ''} onValueChange={v => updateNewRow(row.tempId, 'athlete_id', v)}>
                            <SelectTrigger className="h-9 w-40 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs" data-testid={`new-athlete-select-${row.tempId}`}>
                              <SelectValue placeholder={t('Choose athlete', 'Scegli atleta')} />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-700 max-h-64">
                              {availableToAdd.map(a => (
                                <SelectItem key={a.id} value={a.id} data-testid={`new-athlete-opt-${a.id}`}>
                                  {a.first_name} {a.last_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-1 pr-2">
                          <Input type="number" min="1" value={row.trial_number} onChange={e => updateNewRow(row.tempId, 'trial_number', e.target.value)}
                            className="h-9 w-14 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs" />
                        </td>
                        {isSided && (
                          <td className="py-1 pr-2">
                            <Select value={row.side || ''} onValueChange={v => updateNewRow(row.tempId, 'side', v)}>
                              <SelectTrigger className="h-9 w-20 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-zinc-700">
                                <SelectItem value="RIGHT">R</SelectItem>
                                <SelectItem value="LEFT">L</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        )}
                        <td className="py-1 pr-2">
                          <Input type="number" step="0.5" value={row.load_kg} onChange={e => updateNewRow(row.tempId, 'load_kg', e.target.value)}
                            className="h-9 w-16 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs" />
                        </td>
                        <td className="py-1 pr-2">
                          <Input type="number" step="0.1" value={row.jump_height_cm} onChange={e => updateNewRow(row.tempId, 'jump_height_cm', e.target.value)}
                            className="h-9 w-20 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs"
                            data-testid={`new-height-${row.tempId}`} />
                        </td>
                        {isDropJump && (
                          <td className="py-1 pr-2">
                            <Input type="number" step="1" value={row.contact_time_ms || ''}
                              onChange={e => updateNewRow(row.tempId, 'contact_time_ms', e.target.value)}
                              className="h-9 w-20 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs" />
                          </td>
                        )}
                        <td className="py-1 pr-2">
                          <Input type="number" step="0.01" value={row.rsi} onChange={e => updateNewRow(row.tempId, 'rsi', e.target.value)}
                            className="h-9 w-16 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs" />
                        </td>
                        <td className="py-1 text-right">
                          <Button size="sm" variant="ghost" onClick={() => removeNewRow(row.tempId)}
                            className="text-red-400 hover:text-red-300 h-8 px-2">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
